import React, {useState, useEffect, useRef} from "react";
import {useTranslation} from "react-i18next";
import debounce from "lodash.debounce";
// local
import {CustomTooltip} from "components/CustomTooltip";
import MiniSelect from "components/selects/MiniSelect";
import {Input} from "components/Input";
import ActionSelect from "components/selects/ActionSelect";
import StatusesSelect from "components/selects/StatusesSelect";
import CustomRadioSwitch from "components/CustomRadioSwitch";
import {BulkRfpOperationModal} from "components/modals/BulkRfpOperationModal";
import CustomCheckbox from "components/CustomCheckbox";
import MultipleSelect from "components/selects/MultipleSelect";
import {hasPermission} from "helpers/helper";
import {
  IconUsers,
  IconInbox,
  IconPulse,
  IconEye,
  IconPen,
  IconBubblesMessage,
  IconBriefcase,
  IconCalendarEmpty,
  IconSatellite,
  IconDownload,
} from "components/icons/index";
import {sortProcurementsSelectItems} from "constants/buyer.consts";
import {bulkOperationsProcurementsSelectItems} from "constants/select.consts";
import {bulkProcurementOperations} from "constants/buyer.consts";
import {color} from "constants/color.consts";
import cn from "classnames";

const ProcurementsHead = ({
  activeTab,
  filterParams,
  statuses,
  datesSteps,
  buyerList,
  currentUser,
  counters,
  visibleCheckboxes,
  clickOnSortFilter,
  clickOnSelect,
  clickOnBulkProcurementsUpdate,
  changeVisibleCheckboxes,
}) => {
  const {t} = useTranslation();

  const inputRef = useRef("");

  const [userIds, setUserIds] = useState(
    filterParams?.userId
      ? buyerList?.filter((member) => filterParams?.userId?.includes(member.value))
      : []
  );
  const [bulkOperationModal, setBulkOperationModal] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const DatesStep = ({icon, tooltip, titleColor}) => (
    <div className="project-view-procurements-table_head__dates__step">
      <CustomTooltip
        text={tooltip}
        color={titleColor === "white" ? undefined : color[titleColor]}
        placement={isScrolled ? "bottom" : undefined}
      >
        <div
          className={`project-view-procurements-table_head__dates__step__head-icon ${titleColor}`}
        >
          {icon}
        </div>
      </CustomTooltip>
    </div>
  );

  const RecipientsTooltip = ({text, icon, element}) => (
    <div className={`project-view-procurements-table_head__details__${element}`}>
      <CustomTooltip text={text} placement={isScrolled ? "bottom" : "top"}>
        <span>{icon}</span>
      </CustomTooltip>
    </div>
  );

  const clickOnAction = (action) =>
    setBulkOperationModal(bulkProcurementOperations[action]);

  const inputDebounce = debounce(() => {
    const name = inputRef.current.value?.trim();
    if (name.length) clickOnSortFilter("search", name);
    else clickOnSortFilter("search", undefined);
  }, 600);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 220);
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div
      className={`project-view-procurements-table_head ${isScrolled ? "scrolled" : ""}`}
    >
      <div className="project-view-procurements-table_head__name">
        <CustomCheckbox
          checked={!!counters[0] && counters[0] === counters[1]}
          indeterminate={!!counters[0] && counters[0] !== counters[1]}
          initialButton={visibleCheckboxes}
          onClickInitialButton={changeVisibleCheckboxes}
          onChange={clickOnSelect}
          addClass="mr-2"
        />
        <ActionSelect
          list={bulkOperationsProcurementsSelectItems}
          disabled={!(!!counters[0] && !!counters[1])}
          iconsList={[
            <IconSatellite />,
            <IconCalendarEmpty />,
            <IconBriefcase width={16} height={16} />,
            <IconDownload width={16} height={16} color={color.black} />,
          ]}
          permissionList={[
            ["ENABLE_PROCUREMENTS_BULK_SEND_MESSAGE"],
            ["ENABLE_PROCUREMENTS_BULK_CHANGE_OFFER_DATE"],
            ["ENABLE_PROCUREMENTS_BULK_CHANGE_OFFER_DATE"],
            // ["ENABLE_PROCUREMENTS_BULK_CREATE_REVISION"],
            ["ENABLE_PROCUREMENTS_BULK_EXPORT_TO_NEXT"],
          ]}
          isRelativeCustomContent
          customContent={
            bulkOperationModal && (
              <BulkRfpOperationModal
                action={bulkOperationModal}
                counters={counters}
                currentUser={currentUser}
                onSubmit={clickOnBulkProcurementsUpdate}
                onClose={() => setBulkOperationModal(null)}
              />
            )
          }
          label="PROCUREMENT_BULK_OPERATION_EDIT_LABEL"
          onChange={clickOnAction}
          addClass="mr-10"
        />
        <MiniSelect
          list={sortProcurementsSelectItems}
          selectedValue={filterParams?.sort}
          borderColor={color.black}
          itemAsObject
          onChange={(value) => clickOnSortFilter("sort", value)}
        />
        <MultipleSelect
          title="PROCUREMENT_SELECT_BUYER_LABEL"
          titleInButton
          value={userIds}
          list={buyerList}
          tiny
          uppercaseButton
          initialsSelectionMode
          currentUserId={currentUser?.id}
          color="black"
          realLabels
          onChange={(value) => {
            setUserIds(value);
            clickOnSortFilter(
              "userId",
              value.map((item) => item.value)
            );
          }}
          addClass="ml-4 mr-4"
        />
        <CustomRadioSwitch
          checked={filterParams?.noRft}
          uppercase
          label={t("PROCUREMENT_NO_RFT_LABEL")}
          small
          labelOffset={1}
          isActive={(checked) => clickOnSortFilter("noRft", checked)}
        />
        <Input
          defaultValue={filterParams?.search}
          focus={inputRef}
          tiny
          addClass="ml-4 wdth-200 brr-18"
          flatView
          onChange={inputDebounce}
          placeholder={t("PROCUREMENT_SEARCH_PLACEHOLDER")}
          showClearIcon
          onClear={() => clickOnSortFilter("search", undefined)}
        />
      </div>
      <div className="project-view-procurements-table_head__status">
        <StatusesSelect
          list={statuses}
          selectStatus="PROJECT_ITEM_STATUS_SELECT_STATUS"
          someStatuses="PROJECT_ITEM_STATUS_STATUSES"
          noStatus="PROJECT_ITEM_STATUS_NO_STATUS"
          multiple
          onChange={(value) => {
            clickOnSortFilter("status", value.length === statuses.length ? null : value);
          }}
          defaultValue={filterParams?.status ?? statuses}
        />
      </div>
      {activeTab === 0 && (
        <div
          className={cn("project-view-procurements-table_head__details", {
            "disable-response": !hasPermission(["EDIT_PROCUREMENT_SUPPLIER_RESPONSE"]),
          })}
        >
          <RecipientsTooltip text="READ" icon={<IconEye />} element="icon" />
          <RecipientsTooltip text="INTERESTED" icon={<IconPulse />} element="icon" />
          <RecipientsTooltip text="CONTACT_PERSON" icon={<IconUsers />} element="icon" />
          <RecipientsTooltip text="HAS_OFFERED" icon={<IconInbox />} element="icon" />
          <RecipientsTooltip text="HAS_CONTRACTED" icon={<IconPen />} element="icon" />
          <RecipientsTooltip
            text="MESSAGES"
            icon={<IconBubblesMessage />}
            element="messages"
          />
        </div>
      )}
      {activeTab === 2 && (
        <div className="project-view-procurements-table_head__dates">
          {datesSteps.map((step, index) => {
            const {key, ...restProps} = step;
            return <DatesStep key={`${key}_${index}`} {...restProps} />;
          })}
        </div>
      )}
      {activeTab === 1 && (
        <React.Fragment>
          <div className="project-view-procurements-table_head__pre-cost"></div>
          <div className="project-view-procurements-table_head__cost">{t("BUDGET")}</div>
          <div className="project-view-procurements-table_head__cost">{t("SPENT")}</div>
          <div className="project-view-procurements-table_head__cost">{t("RESULT")}</div>
        </React.Fragment>
      )}
    </div>
  );
};

export default ProcurementsHead;
